/**
 * CastStripBridge — React component (no DOM render) that watches the
 * local cast lifecycle stream and pipes stage updates into
 * castStripState. Mounted from main.tsx alongside the other spectator
 * bridges (KillCamBridge, RemotePlayerVfxBridge, etc.).
 *
 * Stage mapping (from event.label / category / status — see CastStripState
 * for the canonical 5-stage list):
 *
 *   0 intent     —  cast event with label starting 'intent'
 *   1 gate       —  gate-* with status='success' (cooldown/mana/range/PvP/LoS)
 *   2 execute    —  windup-start / execute / 'cast' final emission
 *   3 wire       —  projectile spawn OR vfx:spawn
 *   4 confirmed  —  combat event with status='success' (hit landed)
 *
 * Status drives the rejected/complete signals:
 *   - status changes to 'rejected'  → castStripState.reject()
 *   - status changes to 'completed' → castStripState.complete()
 *   - status changes to 'expired'   → castStripState.complete() (no red)
 *
 * We track the *newest in-flight* group only — that's what's relevant
 * to "where am I in MY current cast". Older groups (still in the stream
 * for the AbilityLifecycleMonitor panel) are ignored here.
 *
 * Gating: only mounts/observes when isNetobsEnabled() — same toggle as
 * the strip primitive's visibility, no point computing if nothing's
 * reading.
 */

import React, { useEffect, useRef } from 'react';
import { useCastLifecycleStream, type LifecycleEvent, type CastGroup } from '@/game/ui/hooks/useCastLifecycleStream';
import { castStripState } from './CastStripState';
import { isNetobsEnabled } from './isNetobsEnabled';

function categorizeStage(event: LifecycleEvent): number {
  const label = (event.label || '').toLowerCase();
  if (event.category === 'combat' && event.status === 'success') return 4;  // confirmed
  if (event.category === 'projectile') return 3;                            // wire
  if (event.category === 'vfx') return 3;                                   // wire (vfx fired)
  if (label.startsWith('windup') || label.startsWith('execute') || label === 'cast') return 2;  // execute
  if (label.startsWith('gate-') && event.status === 'success') return 1;    // gate
  if (label.startsWith('intent')) return 0;                                 // intent
  return -1;  // unmapped — don't advance
}

function newestInFlight(groups: CastGroup[]): CastGroup | null {
  for (let i = groups.length - 1; i >= 0; i--) {
    if (groups[i].status === 'in-flight') return groups[i];
  }
  return null;
}

const CastStripBridgeInner: React.FC = () => {
  const { groups } = useCastLifecycleStream();
  const lastSeenCastIdRef = useRef<string | null>(null);
  const lastSeenStatusRef = useRef<string | null>(null);
  const lastEventCountRef = useRef<number>(0);

  useEffect(() => {
    // Find the cast group that's currently driving the strip:
    // - newest in-flight if any
    // - else newest group that isn't expired-and-old (so completion/
    //   rejection of the just-finished cast still gets seen)
    const inFlight = newestInFlight(groups);
    const driving = inFlight ?? (groups.length > 0 ? groups[groups.length - 1] : null);

    if (!driving) return;

    // New cast detected → reset state + start tracking
    if (driving.castId !== lastSeenCastIdRef.current) {
      castStripState.begin();
      lastSeenCastIdRef.current = driving.castId;
      lastSeenStatusRef.current = driving.status;
      lastEventCountRef.current = 0;
      // Always advance to stage 1 (intent) when a new cast starts —
      // cheap visual cue even if no per-stage events have fired yet.
      castStripState.advanceTo(1);
    }

    // Walk events newer than what we've seen, advance stage accordingly
    const newEvents = driving.events.slice(lastEventCountRef.current);
    for (const ev of newEvents) {
      const stageIdx = categorizeStage(ev);
      if (stageIdx >= 0) {
        // stages 0..4 → fill segments 1..5 (so "0 intent" lights segment 1)
        castStripState.advanceTo(stageIdx + 1);
      }
    }
    lastEventCountRef.current = driving.events.length;

    // Status transitions
    if (driving.status !== lastSeenStatusRef.current) {
      lastSeenStatusRef.current = driving.status;
      if (driving.status === 'rejected') {
        castStripState.reject();
      } else if (driving.status === 'completed') {
        castStripState.complete();
      } else if (driving.status === 'expired') {
        // Expired = lifecycle gave up waiting for further events. Don't
        // advance — let the strip freeze at whatever stage it reached
        // and fade out. Visually distinct from completed (which fills
        // the final purple "confirmed" segment) so DJ can tell the
        // difference between "cast finished cleanly" and "cast got
        // stuck partway through".
        castStripState.complete();
      }
    }
  }, [groups]);

  return null;
};

export const CastStripBridge: React.FC = () => {
  // Don't even mount the inner (which subscribes to the lifecycle
  // stream) when ?netobs=1 isn't set — saves the 60Hz event ingestion
  // when nothing's reading the result.
  if (!isNetobsEnabled()) return null;
  return <CastStripBridgeInner />;
};
